import {Box, HBox} from '../../../../../src/components/core/view';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import React, {useCallback, useEffect, useState} from 'react';
import {
  RecommendedItemCell,
  SubstitutionItemParams,
} from '../../../../../src/itemDetails/types/itemReplacement';
import font, {scaleFonts} from '../../../../../src/appStyles/style/font';
import {isItemOrganic, isWeb} from '@buncha/utils/common';
import {useAppDispatch, useAppSelector} from '@buncha/config/redux/hooks';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../../src/appStyles/hooks/theme';
import {
  useItemReplacementContext,
  useSubstituteItemInRun,
} from '@buncha/itemDetails/hooks/itemReplacement';
import {
  useItemScannerContext,
  useUPCOrder,
} from '@buncha/itemDetails/hooks/ItemScanner';

import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {CustomImage} from '../../../../../src/components/core/Image';
import {H5Text} from '../../../../../src/components/core/text';
import If from '../../../../../src/components/conditional/If';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import ItemName from '@buncha/batchDetails/component/ItemName';
import {ItemScannerGlobalActions} from '@buncha/itemDetails/reducer';
import ItemTypePill from '@buncha/batchDetails/component/ItemName/ItemTypePill';
import {Modules} from '@buncha/utils/types';
import {PButton} from '../../../../../src/components/core/Button';
import RequestReplacementModal from '../requestReplacementModal';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {SubstitutionMode} from '@buncha/itemDetails/types/itemDetails';
import {Touchable} from '@buncha/components/core/touchable';
import {UPCItemLocationStatus} from '@buncha/itemDetails/types/ItemScanner';
import {navigation} from '@buncha/config/navigation';
import {styles} from './styles';
import {useCheckItemToAdd} from '@buncha/batchDetails/hooks/ScannedAddItem';
import {useGetLocale} from '@buncha/localization/hooks';

interface ReplacementItemCellProps {
  itemInfo?: RecommendedItemCell;
  orderItemId?: number;
  orderId?: number;
  runId?: number;
  quantity?: number;
  isUPCMode?: boolean;
  isB2BStore?: boolean;
}

const ReplacementItemCell: React.FC<ReplacementItemCellProps> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const item = props?.itemInfo;
  const name = item?.name || '';
  const imageUrl = item?.itemImageUrl || '';
  const price = (item?.price || 0) / 100;
  const catalogItemId = item?.id || 0;
  const itemSource = item?.itemSource || '';
  const indexPosition = item?.indexPosition || 0;
  const [modalOpen, setModalOpen] = useState(false);
  const {state} = useItemReplacementContext();
  const {button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};
  const {addItemCheckError, checkItem, clearError} = useCheckItemToAdd();
  const {substituteItemInRun, itemSubstituteLoading, errorMessage} =
    useSubstituteItemInRun();
  const storeIndex = state?.item?.order?.run?.store?.elasticSearchIndex;
  const storeId = state?.item?.order?.run?.store?.id;
  const quantityToPick =
    state?.item?.shopperItemChangesForShopper?.updatedQuantity ||
    state?.item?.quantity ||
    0;
  const orderId = state?.item?.order?.id;
  const quantity =
    state?.item?.shopperItemChangesForShopper?.updatedQuantity ||
    state?.item?.quantity ||
    0;
  const orderItemId = state?.item?.id || 0;
  const isWebMode = isWeb();

  const {dispatch} = useItemScannerContext();

  const {substituteItemInUPC} = useUPCOrder(dispatch);
  const [showErrorModal, setSetshowErrorModal] = useState('');

  const {itemLocationData, upcOrderItem, selectedCatalogStoreForUPCRun} =
    useAppSelector(gState => gState.itemScanner);
  const appDispatch = useAppDispatch();

  const toggleModal = useCallback(() => {
    setModalOpen(!modalOpen);
  }, [modalOpen]);

  const handleOnClose = () => {
    setSetshowErrorModal('');
    clearError();
  };

  const onClick = useCallback(() => {
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.ReplacementItemDetailsPage,
      params: {
        catalogItemId: item?.id,
        storeIndex: storeIndex,
        runId: props?.runId,
        quantity: quantityToPick,
        catalogItem: props?.itemInfo,
        orderItem: state?.item,
        storeId: storeId,
      },
    });
  }, [
    item?.id,
    props?.itemInfo,
    props?.runId,
    quantityToPick,
    state?.item,
    storeId,
    storeIndex,
  ]);

  const toggleQuantityModal = useCallback(async () => {
    if (props?.isUPCMode) {
      const data: UPCItemLocationStatus = {
        ...itemLocationData,
        substituionData: {
          name: name,
          orderId: props?.orderId,
          orderItemId: props?.orderItemId,
          position: 0,
          price: Number(Number(Number(price) * 100).toFixed(0)),
          quantity: props?.quantity,
          isCustomItem: false,
          substitutedBy: '',
          imageUrl: item?.itemImageUrl || '',
        },
      };
      navigation.navigate(ShopperScreens.ItemDetails, {
        screen: ItemDetailsScreen.ItemScanner,
        params: {
          orderItemId: upcOrderItem?.id,
          storeIndex: selectedCatalogStoreForUPCRun?.esIndex,
          runId: '',
          quantity: 0,
          isUpcMode: props?.isUPCMode,
          source: 'Substitute Item Cell',
        },
      });
      await substituteItemInUPC(
        {
          upcOrderItemId: upcOrderItem?.id || 0,
          name: name,
          price: Number(Number(Number(price) * 100).toFixed(0)),
          isCustomItem: true,
          itemImageUrl: '',
          note: '',
          catalogItemId: catalogItemId,
        },
        () => {
          if (appDispatch)
            appDispatch(ItemScannerGlobalActions.setItemLocationData(data));
        },
      );
    }
    if (
      !item ||
      !catalogItemId ||
      !orderId ||
      !quantity ||
      !orderItemId ||
      !storeIndex
    )
      return;
    const substituteParams: SubstitutionItemParams = {
      catalogItemId: catalogItemId,
      isCustomItem: false,
      itemImageUrl: item.itemImageUrl,
      name: item.name,
      orderId: orderId,
      position: 0,
      orderItemId: orderItemId,
      price: item.price,
      quantity: quantity, // change to dynamic if required
      substitutedBy: JSON.stringify(item),
      substitutionRequestParmas: {
        substitutionMode: SubstitutionMode.approvedOverCallBeforeRequestSend,
      },
      indexPosition: item.indexPosition,
      itemSource: item.itemSource,
      isPriceSensitive: Boolean(item.flags?.priceSensitive),
      isWeightSensitive: Boolean(item.flags?.weightSensitive),
      metaLoggingForKpi: !props?.isB2BStore
        ? item?.metaLoggingForKpi
        : undefined,
    };
    if (props?.isB2BStore) {
      const res = await checkItem(
        item?.upc,
        orderId,
        storeIndex,
        props?.runId ?? 0,
        item?.metaLoggingForKpi,
      );
      if (res)
        navigation.navigate(ShopperScreens.ScannedAddItemPage, {
          storeIndex: storeIndex,
          runId: props?.runId,
          orderId: orderId,
          storeId: storeId,
          plu: item?.upc,
          substituteParams: substituteParams,
          returnPage: ShopperScreens.BatchDetailsPage,
          quantity,
        });

      return;
    }
    const response = await substituteItemInRun(substituteParams);
    if (response)
      if (isWebMode)
        navigation.navigate(HomeScreens.Shopper, {
          screen: ShopperScreens.BatchDetailsPage,
          params: {runId: props?.runId},
        });
      else
        navigation.navigate(ShopperScreens.ItemDetails, {
          screen: ItemDetailsScreen.ItemScanner,
          params: {
            orderItemId,
            storeIndex,
            runId: props?.runId,
            quantity,
            source: 'Replacement Item Modal',
          },
        });
  }, [
    appDispatch,
    catalogItemId,
    checkItem,
    isWebMode,
    item,
    itemLocationData,
    name,
    orderId,
    orderItemId,
    price,
    props?.isB2BStore,
    props?.isUPCMode,
    props?.orderId,
    props?.orderItemId,
    props?.quantity,
    props?.runId,
    quantity,
    selectedCatalogStoreForUPCRun?.esIndex,
    storeId,
    storeIndex,
    substituteItemInRun,
    substituteItemInUPC,
    upcOrderItem?.id,
  ]);

  useEffect(() => {
    if (errorMessage || addItemCheckError)
      setSetshowErrorModal(errorMessage || addItemCheckError);
  }, [addItemCheckError, errorMessage]);

  return (
    <HBox style={styles.parentBox}>
      <Touchable onPress={onClick}>
        <Box style={styles.imageBox}>
          <CustomImage style={styles.image} source={imageUrl} />
        </Box>
      </Touchable>
      <Box style={styles.textBox}>
        <HBox>
          <Touchable style={styles.namePriceBox} onPress={onClick}>
            {/* <If condition={isItemOrganic(name)}> */}
            <ItemTypePill
              size={font.FontSize.fontH5.fontSize}
              isOrganic={isItemOrganic(name)}
              itemType={item?.itemType}
            />
            {/* </If> */}
            <ItemName name={name} size={font.FontSize.fontH5.fontSize} />
            <H5Text
              alignText="left"
              style={{
                color: theme?.text?.blue?.[300],
                fontFamily: muli?.[700]?.normal,
              }}>
              $ {price}
            </H5Text>
          </Touchable>
          <PButton
            style={styles.replaceCTA}
            title={button?.replace}
            isLoading={itemSubstituteLoading}
            disabled={itemSubstituteLoading}
            loadingIndicator={<SpinnerComponent size={'sm'} />}
            titleStyle={{fontSize: scaleFonts(12)}}
            bgColor={theme?.background?.red?.[100]}
            onPress={toggleQuantityModal}
          />
        </HBox>
      </Box>
      <If condition={modalOpen}>
        <RequestReplacementModal
          catalogItemId={catalogItemId}
          catalogItem={item}
          orderItem={state?.item}
          itemSource={itemSource}
          indexPosition={indexPosition}
          open={modalOpen}
          onClose={toggleModal}
          isB2BStore={props?.isB2BStore}
        />
      </If>
      <If condition={Boolean(showErrorModal)}>
        <AppModal
          isOpen={true}
          onClose={handleOnClose}
          topCTAHandelr={handleOnClose}
          title={'Information!'}
          hideBottomCta
          modalBody={
            <HBox style={styles.modalBody}>
              <H5Text>{showErrorModal}</H5Text>
            </HBox>
          }
          ctaTopTitle={'OK'}
          topCTAColor={theme?.background.green[100]}
          storeId={storeId}
        />
      </If>
    </HBox>
  );
};

export default React.memo(ReplacementItemCell);
